import { useCallback } from "react"
import { useAccountAlias } from "resources/account"
import { useUrlDownload } from "hooks/use-url-download"
import { useAnalyticsWithProperties, EMPTY_ANALYTICS_PROPERTIES } from "connected-components/analytics/context"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { buildArtifactUrl } from "./artifact-content"
import { fileExtension } from "helpers/formatters/file-extension"
import { artifactMediaType, normalizeFileArtifact, type FileArtifact } from "./artifact-contract"

interface UseArtifactDownloadResult {
  downloadArtifact: (threadId: string, artifactId: string) => void
  isDownloading: boolean
}

export function useArtifactDownload(
  artifact: FileArtifact | null,
  artifactThreadId: string | null | undefined,
): UseArtifactDownloadResult {
  const accountAlias = useAccountAlias()
  const analytics = useAnalyticsWithProperties(EMPTY_ANALYTICS_PROPERTIES, "AI Teammate")
  const buildUrl = useCallback(
    (threadId: string, artifactId: string) => buildArtifactUrl({ accountAlias, threadId, artifactId }, "download"),
    [accountAlias],
  )
  const onSuccess = useCallback(() => {
    if (artifact == null) return
    try {
      const { filename, contentType, sizeBytes } = normalizeFileArtifact(artifact)
      const extension = fileExtension(filename)
      analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_DOWNLOADED, {
        artifactId: artifact.id,
        artifactType: artifact.type,
        mediaType: artifactMediaType(contentType),
        ...(extension !== undefined && { extension }),
        ...(sizeBytes !== undefined && { sizeBytes }),
        ...(artifactThreadId != null && { threadId: artifactThreadId }),
      })
    } catch {
      // analytics error must not interrupt the download
    }
  }, [artifact, artifactThreadId, analytics])
  const { download, isDownloading } = useUrlDownload<[string, string]>({
    buildUrl,
    errorMessage: "Unable to download the file. Please try again.",
    onSuccess,
  })
  return { downloadArtifact: download, isDownloading }
}
